/**
 * AffiliateApprovalPage — admin review queue for incoming affiliate
 * applicants. Renders pending applicants with name / company / signup date,
 * Approve and Reject buttons per row, and a status filter.
 *
 * Brand rules: emerald→sky headline gradient, zinc borders, 6–8px rounded,
 * light-theme only, no emojis, no orange/purple/dark accents.
 */
import { useCallback, useMemo, useState, type FormEvent } from "react";
import {
	useAffiliateApprovalQueue,
	useAffiliateApprovalApprove,
	useAffiliateApprovalReject,
} from "./hooks";
import {
	QUEUE_FILTERS,
	computeQueueStats,
	formatInteger,
	formatSignupDate,
	isQueueFilter,
	type AffiliateApprovalRow,
	type QueueFilter,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — aligned with advertiser-approval for visual consistency.
// ---------------------------------------------------------------------------
const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Main component.
// ---------------------------------------------------------------------------

export interface AffiliateApprovalPageProps {
	initialStatus?: QueueFilter;
}

export function AffiliateApprovalPage(
	props: AffiliateApprovalPageProps = {},
) {
	const [status, setStatus] = useState<QueueFilter>(
		props.initialStatus && isQueueFilter(props.initialStatus)
			? props.initialStatus
			: "pending",
	);
	const [country, setCountry] = useState<string>("");
	const [trafficSource, setTrafficSource] = useState<string>("");

	// Reject-reason modal.
	const [rejectTarget, setRejectTarget] = useState<AffiliateApprovalRow | null>(
		null,
	);
	const [rejectReason, setRejectReason] = useState<string>("");

	const queueQuery = useAffiliateApprovalQueue({
		status,
		country: country.trim() || null,
		traffic_source: trafficSource.trim() || null,
		limit: 200,
	});
	const approveMut = useAffiliateApprovalApprove();
	const rejectMut = useAffiliateApprovalReject();

	const rows: readonly AffiliateApprovalRow[] = queueQuery.data?.items ?? [];
	const stats = useMemo(() => computeQueueStats(rows), [rows]);

	const busy = approveMut.isPending || rejectMut.isPending;

	const handleApprove = useCallback(
		(row: AffiliateApprovalRow) => {
			if (busy) return;
			approveMut.mutate(row.id);
		},
		[approveMut, busy],
	);

	const openReject = useCallback((row: AffiliateApprovalRow) => {
		if (row.status !== "pending") return;
		setRejectTarget(row);
		setRejectReason("");
	}, []);

	const handleRejectSubmit = useCallback(
		(e: FormEvent) => {
			e.preventDefault();
			if (!rejectTarget || busy) return;
			const reason = rejectReason.trim();
			if (reason.length < 3) return;
			rejectMut.mutate(
				{ id: rejectTarget.id, reason },
				{
					onSuccess: () => {
						setRejectTarget(null);
						setRejectReason("");
					},
				},
			);
		},
		[busy, rejectMut, rejectReason, rejectTarget],
	);

	const handleRejectCancel = useCallback(() => {
		setRejectTarget(null);
		setRejectReason("");
	}, []);

	const handleResetFilters = useCallback(() => {
		setStatus("pending");
		setCountry("");
		setTrafficSource("");
	}, []);

	return (
		<main
			data-testid="affiliate-approval-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					Affiliates
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
					Affiliate review queue
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Approve or reject incoming affiliate applications. Every decision is
					attributed to your account and recorded in the audit log.
				</p>
			</header>

			<KpiStrip stats={stats} loading={queueQuery.isLoading} />

			<FiltersCard
				status={status}
				onStatusChange={setStatus}
				country={country}
				onCountryChange={setCountry}
				trafficSource={trafficSource}
				onTrafficSourceChange={setTrafficSource}
				onReset={handleResetFilters}
			/>

			<QueueCard
				rows={rows}
				loading={queueQuery.isLoading}
				error={queueQuery.error?.message ?? null}
				busy={busy}
				onApprove={handleApprove}
				onReject={openReject}
			/>

			{rejectTarget ? (
				<RejectReasonModal
					row={rejectTarget}
					value={rejectReason}
					onChange={setRejectReason}
					onCancel={handleRejectCancel}
					onSubmit={handleRejectSubmit}
					submitting={rejectMut.isPending}
					errorMessage={rejectMut.error?.message ?? null}
				/>
			) : null}
		</main>
	);
}

export default AffiliateApprovalPage;

// ---------------------------------------------------------------------------
// Sub-components.
// ---------------------------------------------------------------------------

function KpiStrip(props: {
	stats: { total: number; pending: number; approved: number; rejected: number };
	loading: boolean;
}) {
	const cells = [
		{ label: "Total", value: props.stats.total },
		{ label: "Pending", value: props.stats.pending },
		{ label: "Approved", value: props.stats.approved },
		{ label: "Rejected", value: props.stats.rejected },
	];
	return (
		<section
			aria-label="Queue statistics"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
			data-testid="affiliate-approval-kpi"
		>
			{cells.map((cell) => (
				<div
					key={cell.label}
					className={`${CARD} flex flex-col gap-1 p-4`}
					data-testid={`affiliate-approval-kpi-${cell.label.toLowerCase()}`}
				>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{cell.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : formatInteger(cell.value)}
					</p>
				</div>
			))}
		</section>
	);
}

function FiltersCard(props: {
	status: QueueFilter;
	onStatusChange: (v: QueueFilter) => void;
	country: string;
	onCountryChange: (v: string) => void;
	trafficSource: string;
	onTrafficSourceChange: (v: string) => void;
	onReset: () => void;
}) {
	return (
		<section
			className={`${CARD} flex flex-col gap-4 p-5`}
			data-testid="affiliate-approval-filters"
		>
			<h2 className="text-base font-semibold text-zinc-900">Filters</h2>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<label className="flex flex-col gap-1.5">
					<span className={LABEL}>Status</span>
					<select
						className={SELECT}
						value={props.status}
						onChange={(e) =>
							props.onStatusChange(e.currentTarget.value as QueueFilter)
						}
						data-testid="affiliate-approval-filter-status"
					>
						{QUEUE_FILTERS.map((f) => (
							<option key={f} value={f}>
								{f.charAt(0).toUpperCase() + f.slice(1)}
							</option>
						))}
					</select>
				</label>
				<label className="flex flex-col gap-1.5">
					<span className={LABEL}>Country</span>
					<input
						type="text"
						className={INPUT}
						placeholder="US"
						value={props.country}
						onChange={(e) => props.onCountryChange(e.currentTarget.value)}
						data-testid="affiliate-approval-filter-country"
					/>
				</label>
				<label className="flex flex-col gap-1.5">
					<span className={LABEL}>Traffic source</span>
					<input
						type="text"
						className={INPUT}
						placeholder="seo"
						value={props.trafficSource}
						onChange={(e) =>
							props.onTrafficSourceChange(e.currentTarget.value)
						}
						data-testid="affiliate-approval-filter-traffic"
					/>
				</label>
			</div>
			<div className="flex justify-end">
				<button
					type="button"
					onClick={props.onReset}
					className={BTN_NEUTRAL}
					data-testid="affiliate-approval-filter-reset"
				>
					Reset filters
				</button>
			</div>
		</section>
	);
}

function QueueCard(props: {
	rows: readonly AffiliateApprovalRow[];
	loading: boolean;
	error: string | null;
	busy: boolean;
	onApprove: (row: AffiliateApprovalRow) => void;
	onReject: (row: AffiliateApprovalRow) => void;
}) {
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="affiliate-approval-queue"
		>
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">Applicants</h2>
					<p className="text-sm text-zinc-500">
						{props.rows.length === 0
							? "Nothing to review for the current filters."
							: `${props.rows.length} applicant${props.rows.length === 1 ? "" : "s"} in view.`}
					</p>
				</div>
			</header>

			{props.error ? (
				<div
					className="p-5 text-sm text-red-600"
					role="alert"
					data-testid="affiliate-approval-error"
				>
					{props.error}
				</div>
			) : props.loading ? (
				<div
					className="p-5 text-sm text-zinc-500"
					data-testid="affiliate-approval-loading"
				>
					Loading applicants.
				</div>
			) : props.rows.length === 0 ? (
				<div
					className="flex flex-col items-center gap-1 p-10 text-center"
					data-testid="affiliate-approval-empty"
				>
					<p className="text-sm font-medium text-zinc-700">
						No applicants match the current filters.
					</p>
					<p className="text-xs text-zinc-500">
						New signups appear here as they come in.
					</p>
				</div>
			) : (
				<QueueTable
					rows={props.rows}
					busy={props.busy}
					onApprove={props.onApprove}
					onReject={props.onReject}
				/>
			)}
		</section>
	);
}

function QueueTable(props: {
	rows: readonly AffiliateApprovalRow[];
	busy: boolean;
	onApprove: (row: AffiliateApprovalRow) => void;
	onReject: (row: AffiliateApprovalRow) => void;
}) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="affiliate-approval-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>Applicant</Th>
						<Th>Company</Th>
						<Th>Country</Th>
						<Th>Traffic</Th>
						<Th>Signup</Th>
						<Th>Status</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{props.rows.map((row) => (
						<tr
							key={row.id}
							data-testid={`affiliate-approval-row-${row.id}`}
							className="hover:bg-zinc-50/80"
						>
							<Td>
								<div className="flex flex-col">
									<span className="font-medium text-zinc-900">{row.name}</span>
									<span className="text-xs text-zinc-500">{row.email}</span>
								</div>
							</Td>
							<Td>{row.company ?? "—"}</Td>
							<Td>{row.country ?? "—"}</Td>
							<Td>{row.traffic_source ?? "—"}</Td>
							<Td mono>{formatSignupDate(row.created_at)}</Td>
							<Td>
								<StatusBadge status={row.status} />
							</Td>
							<Td className="text-right">
								<div className="inline-flex gap-2">
									<button
										type="button"
										onClick={() => props.onApprove(row)}
										disabled={props.busy || row.status !== "pending"}
										className={BTN_PRIMARY}
										data-testid={`affiliate-approval-approve-${row.id}`}
									>
										Approve
									</button>
									<button
										type="button"
										onClick={() => props.onReject(row)}
										disabled={props.busy || row.status !== "pending"}
										className={BTN_DANGER}
										data-testid={`affiliate-approval-reject-${row.id}`}
									>
										Reject
									</button>
								</div>
							</Td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function RejectReasonModal(props: {
	row: AffiliateApprovalRow;
	value: string;
	onChange: (v: string) => void;
	onCancel: () => void;
	onSubmit: (e: FormEvent) => void;
	submitting: boolean;
	errorMessage: string | null;
}) {
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby="affiliate-approval-reject-title"
			data-testid="affiliate-approval-reject-modal"
			onKeyDown={(e) => {
				if (e.key === "Escape") props.onCancel();
			}}
		>
			<form
				onSubmit={props.onSubmit}
				className={`${CARD} flex w-full max-w-md flex-col gap-4 p-6`}
			>
				<header className="flex flex-col gap-1">
					<h2
						id="affiliate-approval-reject-title"
						className="text-base font-semibold text-zinc-900"
					>
						Reject applicant
					</h2>
					<p className="text-xs text-zinc-500">
						<span className="font-medium">{props.row.name}</span> will be marked
						rejected. Provide a reason (minimum 3 characters) for the audit log.
					</p>
				</header>
				<label className="flex flex-col gap-1.5">
					<span className={LABEL}>Reason</span>
					<textarea
						className={`${INPUT} min-h-[6rem]`}
						value={props.value}
						onChange={(e) => props.onChange(e.currentTarget.value)}
						autoFocus
						maxLength={1000}
						data-testid="affiliate-approval-reject-reason"
					/>
					<span className="text-xs text-zinc-400">
						{props.value.length}/1000
					</span>
				</label>
				{props.errorMessage ? (
					<p className="text-xs text-red-600" role="alert">
						{props.errorMessage}
					</p>
				) : null}
				<div className="flex items-center justify-end gap-2">
					<button
						type="button"
						onClick={props.onCancel}
						className={BTN_NEUTRAL}
						disabled={props.submitting}
						data-testid="affiliate-approval-reject-cancel"
					>
						Cancel
					</button>
					<button
						type="submit"
						className={BTN_DANGER}
						disabled={props.submitting || props.value.trim().length < 3}
						data-testid="affiliate-approval-reject-submit"
					>
						{props.submitting ? "Rejecting." : "Reject"}
					</button>
				</div>
			</form>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Small primitives.
// ---------------------------------------------------------------------------

function StatusBadge(props: { status: AffiliateApprovalRow["status"] }) {
	const tone =
		props.status === "approved"
			? "border-emerald-200 bg-emerald-50 text-emerald-700"
			: props.status === "rejected"
				? "border-red-200 bg-red-50 text-red-700"
				: props.status === "suspended"
					? "border-zinc-300 bg-zinc-100 text-zinc-700"
					: "border-sky-200 bg-sky-50 text-sky-700";
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${tone}`}
			data-testid={`affiliate-approval-status-${props.status}`}
		>
			{props.status.charAt(0).toUpperCase() + props.status.slice(1)}
		</span>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th
			scope="col"
			className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}
		>
			{props.children}
		</th>
	);
}

function Td(props: {
	children: React.ReactNode;
	className?: string;
	mono?: boolean;
}) {
	const base = "px-4 py-3 align-top text-zinc-700";
	const cls = [base, props.mono ? "font-mono text-xs" : "", props.className ?? ""]
		.filter(Boolean)
		.join(" ");
	return <td className={cls}>{props.children}</td>;
}
